// Docs: FEATURES.md "App structure" (wake screen game). Mini-games shown on
// the wake screen (WakeGate.jsx) while the api boots — der/die/das, Plural and
// one-tap versions of the grammar drills (Perfekt, Präteritum, Präposition,
// Kasus, Reflexiv, Verb + Präposition, Nomen-Verb). Each wake screen starts on
// a random game (never last time's); tabs or the "Anderes Spiel" button switch.
// Fully offline: bundled decks (utils/wakeGames.js), one top list of finished
// streaks per game in localStorage (utils/wakeLeaderboard.js) — the live run
// is ranked against it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ARTICLES, WAKE_GAMES, gameById, pickGame } from '../utils/wakeGames';
import { readTop, recordRun, standing, writeTop } from '../utils/wakeLeaderboard';

export { ARTICLES };

// A miss stays up longer so the correction and rule can be read; tapping the
// card (or Space / →) skips ahead either way.
const NEXT_AFTER_CORRECT_MS = 700;
const NEXT_AFTER_WRONG_MS = 2600;
const MILESTONES = new Set([5, 10, 15, 20, 30, 50]);
const LADDER_SIZE = 5;
// Last game shown, so the next wake screen starts on a different one.
export const GAME_PICK_KEY = 'gt-wake-game';

export const ArticleShape = ({ shape, className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    {shape === 'circle' && <circle cx="12" cy="12" r="10" />}
    {shape === 'triangle' && <polygon points="12,2 22,22 2,22" />}
    {shape === 'square' && <rect x="2" y="2" width="20" height="20" />}
  </svg>
);

// ✓ / ✗ marks, so right and wrong never rely on colour alone (die is red and
// das is green on the article buttons).
const Mark = ({ right, className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    {right ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />}
  </svg>
);

// Stored runs closest to the top, with the live run slotted in at its place.
function ladderFor(top, streak, rank) {
  const rows = top.slice(0, LADDER_SIZE).map((value, i) => ({ value, key: `t${i}` }));
  if (rank && rank <= LADDER_SIZE) rows.splice(rank - 1, 0, { value: streak, key: 'live', live: true });
  return rows.slice(0, LADDER_SIZE);
}

function targetText({ record, need, place }, hadRuns) {
  if (record) return hadRuns ? 'Neuer Rekord!' : 'Deine erste Serie!';
  if (need == null) return null;
  return `noch ${need} bis ${place === 1 ? 'zum Rekord' : `Platz ${place}`}`;
}

const readPick = () => {
  try {
    return window.localStorage.getItem(GAME_PICK_KEY);
  } catch {
    return null;
  }
};

const rememberPick = (id) => {
  try {
    window.localStorage.setItem(GAME_PICK_KEY, id);
  } catch { /* the next pick just may repeat this one */ }
};

// `startWith` pins the first game (tests); otherwise it is random.
export default function WakeGame({ onPlay, random = Math.random, startWith }) {
  const [gameId, setGameId] = useState(() => (startWith ? gameById(startWith) : pickGame(readPick(), random)).id);
  const tabsRef = useRef(null);
  useEffect(() => { rememberPick(gameId); }, [gameId]);
  // Keep the active tab visible in the scrolling tab row.
  useEffect(() => {
    tabsRef.current?.querySelector('.is-active')?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [gameId]);
  // Fade whichever edge has more tabs beyond it, so a tab sliced by the edge
  // reads as "scroll for more" rather than broken layout.
  const [edges, setEdges] = useState({ left: false, right: false });
  const updateEdges = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);
  useEffect(() => {
    updateEdges();
    const el = tabsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateEdges]);

  const game = gameById(gameId);
  return (
    <div className="wake-games">
      <div className="wake-games__bar">
        <div
          className={`wake-games__tabs${edges.left ? ' wake-games__tabs--fade-left' : ''}${edges.right ? ' wake-games__tabs--fade-right' : ''}`}
          role="tablist"
          aria-label="Spiel wählen"
          ref={tabsRef}
          onScroll={updateEdges}
        >
          {WAKE_GAMES.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={g.id === gameId}
              className={`wake-games__tab ${g.id === gameId ? 'is-active' : ''}`}
              onClick={() => setGameId(g.id)}
            >
              {g.tab}
            </button>
          ))}
        </div>
      </div>
      {/* Remounting on switch ends (and records) the current run. */}
      <GameRunner
        key={game.id}
        game={game}
        onPlay={onPlay}
        random={random}
        onAnother={() => setGameId(pickGame(gameId, random).id)}
      />
    </div>
  );
}

function GameRunner({ game, onPlay, random, onAnother }) {
  const deck = useMemo(() => game.rounds(random), [game, random]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null); // value the player chose
  const [streak, setStreak] = useState(0);
  const [score, setScore] = useState({ right: 0, total: 0 });
  const [top, setTop] = useState(() => readTop(game.storageKey));
  const [milestone, setMilestone] = useState(null);
  const [lost, setLost] = useState(0); // streak the last miss ended
  const timer = useRef(null);

  // A run still going when the game goes away (gate lifted, tab closed,
  // another game picked) counts as finished.
  const streakRef = useRef(0);
  streakRef.current = streak;
  useEffect(() => {
    const flush = () => {
      if (streakRef.current > 0) {
        writeTop(recordRun(readTop(game.storageKey), streakRef.current), game.storageKey);
      }
      streakRef.current = 0;
    };
    window.addEventListener('pagehide', flush);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [game.storageKey]);

  const round = deck[index % deck.length];
  const answered = picked !== null;
  const correct = picked === round.answer;

  const advance = useCallback(() => {
    clearTimeout(timer.current);
    setPicked(null);
    setIndex((i) => i + 1);
  }, []);

  const answer = useCallback((choice) => {
    if (answered) return;
    onPlay?.();
    const isRight = choice === round.answer;
    setPicked(choice);
    setScore((s) => ({ right: s.right + (isRight ? 1 : 0), total: s.total + 1 }));
    if (isRight) {
      const next = streak + 1;
      setStreak(next);
      const before = standing(top, streak);
      const after = standing(top, next);
      const climbed = after.rank && (!before.rank || after.rank < before.rank) && top.length > 0;
      if (after.record && !before.record && top.length > 0) setMilestone('Neuer Rekord!');
      else if (climbed) setMilestone(`Platz ${after.rank}!`);
      else setMilestone(MILESTONES.has(next) ? `${next} in Folge!` : null);
    } else {
      const updated = recordRun(top, streak);
      setTop(updated);
      writeTop(updated, game.storageKey);
      setLost(streak);
      streakRef.current = 0;
      setStreak(0);
      setMilestone(null);
    }
    timer.current = setTimeout(advance, isRight ? NEXT_AFTER_CORRECT_MS : NEXT_AFTER_WRONG_MS);
  }, [advance, answered, game.storageKey, onPlay, round, streak, top]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (answered && (e.key === ' ' || e.key === 'ArrowRight')) {
        e.preventDefault();
        advance();
        return;
      }
      const i = ['1', '2', '3'].indexOf(e.key);
      if (i >= 0 && round.choices[i]) answer(round.choices[i].value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, answer, answered, round]);

  const stand = standing(top, streak);
  const ladder = ladderFor(top, streak, stand.rank);
  const target = targetText(stand, top.length > 0);
  const { reveal } = round;

  return (
    <section className={`wake-game wake-game--${game.id}`} aria-label={game.title}>
      <header className="wake-game__head">
        <span className="wake-game__label">{game.title}</span>
        <span className="wake-game__stats">
          <span
            className={`wake-game__streak ${milestone ? 'is-milestone' : ''} ${streak > 0 ? 'is-up' : ''} ${answered && !correct && lost > 0 ? 'is-broken' : ''}`}
            key={`s${streak}-${index}`}
          >
            Serie {streak}
          </span>
          {stand.rank && (
            <>
              <span aria-hidden="true"> · </span>
              <span className="wake-game__rank" key={`r${stand.rank}`}>Platz {stand.rank}</span>
            </>
          )}
        </span>
      </header>

      {(ladder.length > 0 || target) && (
        <div className="wake-game__board">
          <ol className="wake-game__ladder" aria-label="Deine besten Serien">
            {ladder.map((row) => (
              <li key={row.key} className={row.live ? 'is-live' : ''}>
                {row.value}
              </li>
            ))}
            {streak > 0 && !ladder.some((row) => row.live) && (
              <li className="is-live is-off">{streak}</li>
            )}
          </ol>
          {target && <p className="wake-game__target">{target}</p>}
        </div>
      )}

      {/* Once answered, a tap on the card skips the wait for the next one. */}
      <div
        className={`wake-game__card ${answered ? (correct ? 'is-right' : 'is-wrong') : ''}`}
        key={index}
        data-testid="wake-game-card"
        onClick={answered ? advance : undefined}
      >
        {round.cue && <p className="wake-game__cue">{round.cue}</p>}
        <p className={`wake-game__noun ${round.long ? 'is-long' : ''}`}>
          {answered ? (
            <>
              {reveal.pre}
              <span className={`wake-game__article wake-game__article--${reveal.tone || 'fill'}`}>
                {reveal.fill.trimEnd()}
              </span>
              {reveal.fill.endsWith(' ') && ' '}
              {reveal.post}
            </>
          ) : round.prompt}
        </p>
        <div className="wake-game__feedback" aria-live="polite">
          {answered && (
            <>
              <p className={`wake-game__verdict ${correct ? 'is-right' : 'is-wrong'}`}>
                <Mark right={correct} className="wake-game__verdict-mark" />
                {correct ? 'Richtig!' : 'Leider falsch'}
                {!correct && lost > 0 && <span className="wake-game__verdict-note">Serie {lost} beendet</span>}
              </p>
              {round.detail && <p className="wake-game__detail">{round.detail}</p>}
              {!correct && <p className="wake-game__skip">Tippen für weiter</p>}
            </>
          )}
        </div>
        {milestone && answered && correct && (
          <p className="wake-game__milestone" aria-hidden="true">{milestone}</p>
        )}
        {answered && (
          <span
            className="wake-game__timer"
            style={{ '--wait': `${correct ? NEXT_AFTER_CORRECT_MS : NEXT_AFTER_WRONG_MS}ms` }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className={`wake-game__choices ${round.choices[0].shape ? '' : 'is-text'}`}>
        {round.choices.map(({ value, label, tone, shape }, i) => {
          const state = !answered
            ? ''
            : value === round.answer
              ? 'is-answer'
              : value === picked
                ? 'is-miss'
                : 'is-dim';
          return (
            <button
              key={value}
              type="button"
              className={`wake-game__choice wake-game__choice--${tone || 'text'} ${state}`}
              onClick={() => answer(value)}
              disabled={answered}
              aria-keyshortcuts={String(i + 1)}
            >
              {shape && <ArticleShape shape={shape} className="wake-game__choice-shape" />}
              <span>{label}</span>
              {(state === 'is-answer' || state === 'is-miss') && (
                <Mark right={state === 'is-answer'} className="wake-game__choice-mark" />
              )}
            </button>
          );
        })}
      </div>

      <div className="wake-game__foot">
        <span>{score.total > 0 ? `${score.right} von ${score.total} richtig` : 'Tippe oder drücke 1 · 2 · 3'}</span>
        <button type="button" className="wake-game__another" onClick={onAnother}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8" cy="8" r="1.7" /><circle cx="16" cy="16" r="1.7" /><circle cx="12" cy="12" r="1.7" />
            <circle cx="16" cy="8" r="1.7" /><circle cx="8" cy="16" r="1.7" />
          </svg>
          Anderes Spiel
        </button>
      </div>
    </section>
  );
}
